import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FiChevronDown, FiCheck } from 'react-icons/fi';

import {
  Container,
  Trigger,
  TriggerLabel,
  Placeholder,
  Panel,
  Option,
  OptionLabel,
  EmptyOption,
} from './styles';

export interface DropdownOption {
  value: string;
  label: string;
  /** Texto secundário opcional (ex.: o id do curso). */
  hint?: string;
}

interface DropdownProps {
  /** Rótulo visível do campo. */
  label?: string;
  /** Texto exibido quando nada está selecionado. */
  placeholder?: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Mensagem exibida quando `options` está vazio. */
  emptyMessage?: string;
  id?: string;
}

/**
 * Select customizado do admin — substitui o `<select>` nativo.
 *
 * Acessibilidade: `role="combobox"` + `aria-expanded` + `aria-controls` no
 * gatilho, `role="listbox"`/`role="option"` no painel, navegação por setas,
 * Home/End, Enter/Espaço para escolher e Esc para fechar. O foco volta ao
 * gatilho ao fechar.
 */
const Dropdown: React.FC<DropdownProps> = ({
  label,
  placeholder = 'Selecione…',
  options,
  value,
  onChange,
  disabled = false,
  emptyMessage = 'Nenhuma opção disponível',
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const generatedId = useId();
  const triggerId = id ?? `dropdown-${generatedId}`;
  const listboxId = `${triggerId}-listbox`;

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selected = options.find((option) => option.value === value);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    triggerRef.current?.focus();
  }, []);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    const currentIndex = options.findIndex((option) => option.value === value);
    setActiveIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [disabled, options, value]);

  const choose = useCallback(
    (option: DropdownOption) => {
      onChange(option.value);
      close();
    },
    [onChange, close],
  );

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Mantém o item ativo visível.
  useEffect(() => {
    if (open && activeIndex >= 0) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!open) openPanel();
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      default:
        break;
    }
  };

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(options.length - 1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const option = options[activeIndex];
        if (option) choose(option);
        break;
      }
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <Container ref={containerRef}>
      {label && (
        <label htmlFor={triggerId} id={`${triggerId}-label`}>
          {label}
        </label>
      )}

      <Trigger
        ref={triggerRef}
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={label ? `${triggerId}-label` : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onTriggerKeyDown}
      >
        <TriggerLabel>
          {selected ? selected.label : <Placeholder>{placeholder}</Placeholder>}
        </TriggerLabel>
        <FiChevronDown aria-hidden className={open ? 'open' : undefined} />
      </Trigger>

      {open && (
        <Panel
          id={listboxId}
          role="listbox"
          aria-labelledby={label ? `${triggerId}-label` : undefined}
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
          ref={(node) => node?.focus()}
        >
          {options.length === 0 && <EmptyOption>{emptyMessage}</EmptyOption>}

          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = index === activeIndex;

            return (
              <Option
                key={option.value}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                aria-selected={isSelected}
                active={isActive}
                selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(option)}
              >
                <OptionLabel>
                  {option.label}
                  {option.hint && <small>{option.hint}</small>}
                </OptionLabel>
                {isSelected && <FiCheck aria-hidden />}
              </Option>
            );
          })}
        </Panel>
      )}
    </Container>
  );
};

export default Dropdown;
